// FILE: src/components/FoundingTesterAdmin.js
//
// Founding Testers — Mission Control admin tool. Curated recognition only:
// administrators award / edit / remove Founding Tester status by hand. Backed
// by the admin_* RPCs in migration 050 (all re-check admin server-side).
//
// Capabilities: search users, award, edit contribution notes / discord /
// display-name override, remove, and preview the public Founder Wall.
//
// No Redux. Functional component, hooks only. Inline styles match the other
// Mission Control screens (RegisteredUsersScreen pattern).

import React, { useCallback, useEffect, useState } from 'react';
import {
  adminListFoundingTesters,
  adminFoundingCandidates,
  adminSearchUsersForFounding,
  adminUpsertFoundingTester,
  adminRemoveFoundingTester,
} from '../../services/foundingTesterService';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function openWallPreview() {
  try {
    window.open(`${window.location.pathname}?view=founding-testers`, '_blank', 'noopener');
  } catch (_) {
    window.dispatchEvent(new CustomEvent('pm-pixel:navigate', { detail: { view: 'founding-testers' } }));
  }
}

export default function FoundingTesterAdmin({ onBack }) {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Candidates — registered via the landing page (founding_tester achievement)
  // AND completed >=1 exam session. Helps the admin identify who to recognize.
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(true);

  // Search
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = not searched
  const [searching, setSearching] = useState(false);

  // Award / edit form — keyed by user_id; null when closed.
  const [form, setForm] = useState(null); // { userId, email, name, notes, discord, displayOverride, mode }
  const [saving, setSaving] = useState(false);

  const loadRoster = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRoster(await adminListFoundingTesters());
    } catch (e) {
      setError(e?.message || 'Failed to load Founding Testers');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCandidates = useCallback(async () => {
    setCandLoading(true);
    try {
      setCandidates(await adminFoundingCandidates());
    } catch (e) {
      // Non-fatal: the roster is the primary tool. Surface, but don't block it.
      setError((prev) => prev || e?.message || 'Failed to load candidates');
    } finally {
      setCandLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadRoster(), loadCandidates()]);
  }, [loadRoster, loadCandidates]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const runSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    setSearching(true);
    setError(null);
    try {
      setResults(await adminSearchUsersForFounding(query));
    } catch (err) {
      setError(err?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [query]);

  function openAward(u) {
    setForm({
      userId: u.user_id,
      email: u.email,
      name: u.display_name || '',
      notes: '',
      discord: '',
      displayOverride: '',
      mode: 'award',
    });
  }

  function openEdit(r) {
    setForm({
      userId: r.user_id,
      email: r.email,
      name: r.display_name || '',
      notes: r.contribution_notes || '',
      discord: r.discord_username || '',
      displayOverride: r.display_name_override || '',
      mode: 'edit',
    });
  }

  async function submitForm(e) {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      await adminUpsertFoundingTester({
        userId: form.userId,
        notes: form.notes,
        discord: form.discord,
        displayOverride: form.displayOverride,
      });
      setForm(null);
      await Promise.all([loadRoster(), loadCandidates()]);
      if (results) await runSearch();
    } catch (err) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function remove(r) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove Founding Tester status for ${r.email}?`)) return;
    setError(null);
    try {
      await adminRemoveFoundingTester(r.user_id);
      await Promise.all([loadRoster(), loadCandidates()]);
      if (results) await runSearch();
    } catch (err) {
      setError(err?.message || 'Remove failed');
    }
  }

  return (
    <div data-testid="founding-tester-admin" style={S.wrapper}>
      <div style={S.header}>
        <button data-testid="founding-tester-admin-back" style={S.backButton} onClick={onBack}>← Back</button>
        <span style={S.title}>🏛️ Founding Testers</span>
        <span style={{ flex: 1 }} />
        <button style={S.headerAction} onClick={refreshAll} disabled={loading || candLoading}>⟳ Refresh</button>
        <button style={S.headerAction} onClick={openWallPreview}>↗ Preview Founder Wall</button>
      </div>

      <div style={S.body}>
        <p style={S.intro}>
          Curated recognition for original beta contributors. Never automatic —
          award by hand to the learners who helped shape PM Pixel.
        </p>

        {error && <div style={S.error}>{error}</div>}

        {/* ── Candidates — registered via landing page + completed an exam ─ */}
        <div style={S.card} data-testid="founding-tester-candidates">
          <h3 style={S.cardTitle}>
            Registered &amp; Active Candidates{' '}
            {!candLoading && <span style={S.count}>({candidates.length})</span>}
          </h3>
          <p style={S.cardHint}>
            Registered through the beta landing page <em>and</em> completed at least
            one exam session (Full, Practice or Targeted). These are the learners
            most worth recognizing — award straight from here.
          </p>
          {candLoading && <div style={S.note}>Loading…</div>}
          {!candLoading && candidates.length === 0 && (
            <div style={S.note}>No qualifying registrants yet.</div>
          )}
          {!candLoading && candidates.length > 0 && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Registered</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Exams</th>
                  <th style={S.th}>Last exam</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.user_id} data-testid="founding-tester-candidate-row">
                    <td style={S.td}>
                      <div style={S.resultName}>{c.display_name || '(no name)'}</div>
                      <div style={S.resultEmail}>{c.email}</div>
                    </td>
                    <td style={S.td}>{fmtDate(c.registered_at)}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {c.exam_sessions_count}
                    </td>
                    <td style={S.td}>{fmtDate(c.last_exam_at)}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {c.is_founding_tester ? (
                        <span style={S.tag}>Founding Tester</span>
                      ) : (
                        <button
                          style={S.smallBtn}
                          onClick={() => openAward(c)}
                          data-testid="founding-tester-candidate-award-btn"
                        >
                          + Award
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Search & award ─────────────────────────────────────────── */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>Award a Founding Tester</h3>
          <form onSubmit={runSearch} style={S.searchRow}>
            <input
              style={S.input}
              placeholder="Search by email or display name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="founding-tester-search-input"
            />
            <button style={S.primaryBtn} type="submit" disabled={searching}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </form>

          {results && results.length === 0 && (
            <div style={S.note}>No matching users.</div>
          )}
          {results && results.length > 0 && (
            <ul style={S.resultList}>
              {results.map((u) => (
                <li key={u.user_id} style={S.resultRow}>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.resultName}>{u.display_name || '(no name)'}</div>
                    <div style={S.resultEmail}>{u.email}</div>
                  </div>
                  <span style={{ flex: 1 }} />
                  {u.is_founding_tester ? (
                    <span style={S.tag}>Founding Tester</span>
                  ) : (
                    <button style={S.smallBtn} onClick={() => openAward(u)}
                      data-testid="founding-tester-award-btn">+ Award</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Current roster ─────────────────────────────────────────── */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>
            Current Founding Testers {!loading && <span style={S.count}>({roster.length})</span>}
          </h3>
          {loading && <div style={S.note}>Loading…</div>}
          {!loading && roster.length === 0 && (
            <div style={S.note}>None yet. Search above to award the first one.</div>
          )}
          {!loading && roster.length > 0 && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Contribution</th>
                  <th style={S.th}>Discord</th>
                  <th style={S.th}>Awarded</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {roster.map((r) => (
                  <tr key={r.user_id}>
                    <td style={S.td}>
                      <div style={S.resultName}>{r.display_name_override || r.display_name || '(no name)'}</div>
                      <div style={S.resultEmail}>{r.email}</div>
                    </td>
                    <td style={S.td}>{r.contribution_notes || <span style={S.muted}>—</span>}</td>
                    <td style={S.td}>{r.discord_username || <span style={S.muted}>—</span>}</td>
                    <td style={S.td}>{fmtDate(r.awarded_at)}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      <button style={S.smallBtn} onClick={() => openEdit(r)}>Edit</button>
                      <button style={S.dangerBtn} onClick={() => remove(r)}
                        data-testid="founding-tester-remove-btn">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Award / edit modal ───────────────────────────────────────── */}
      {form && (
        <div style={S.overlay} onClick={() => !saving && setForm(null)}>
          <form style={S.modal} onClick={(e) => e.stopPropagation()} onSubmit={submitForm}>
            <h3 style={S.cardTitle}>
              {form.mode === 'award' ? 'Award Founding Tester' : 'Edit Founding Tester'}
            </h3>
            <div style={S.modalWho}>{form.name || form.email} <span style={S.resultEmail}>{form.email}</span></div>

            <label style={S.label}>Contribution note</label>
            <textarea
              style={S.textarea}
              rows={2}
              placeholder="e.g. Provided early pacing feedback"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />

            <label style={S.label}>Discord username</label>
            <input
              style={S.input}
              placeholder="e.g. bryan#1234"
              value={form.discord}
              onChange={(e) => setForm({ ...form, discord: e.target.value })}
            />

            <label style={S.label}>Display name override (optional)</label>
            <input
              style={S.input}
              placeholder="Shown on the Founder Wall instead of profile name"
              value={form.displayOverride}
              onChange={(e) => setForm({ ...form, displayOverride: e.target.value })}
            />

            <div style={S.modalActions}>
              <button type="button" style={S.smallBtn} onClick={() => setForm(null)} disabled={saving}>Cancel</button>
              <button type="submit" style={S.primaryBtn} disabled={saving}
                data-testid="founding-tester-save-btn">
                {saving ? 'Saving…' : form.mode === 'award' ? 'Award' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const S = {
  wrapper: { minHeight: '100vh', background: '#f4f5f8', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", color: '#1d1d29' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px', background: '#14142a', color: '#fff', position: 'sticky', top: 0, zIndex: 10 },
  title: { fontWeight: 700, fontSize: '1rem', letterSpacing: '0.3px' },
  backButton: { background: 'transparent', border: '1px solid #44445e', borderRadius: 6, color: '#bcbcd0', padding: '5px 13px', cursor: 'pointer', fontSize: '0.8rem' },
  headerAction: { background: 'transparent', border: '1px solid #44445e', borderRadius: 6, color: '#bcbcd0', padding: '5px 13px', cursor: 'pointer', fontSize: '0.8rem' },
  body: { maxWidth: 980, margin: '0 auto', padding: '22px 24px 60px' },
  intro: { color: '#5a6582', fontSize: '0.92rem', margin: '0 0 16px', maxWidth: 640 },
  card: { background: '#fff', border: '1px solid #e6e6ea', borderRadius: 14, padding: '18px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(20,20,40,0.06)' },
  cardTitle: { margin: '0 0 14px', fontSize: '1rem', fontWeight: 800 },
  cardHint: { margin: '-6px 0 14px', color: '#5a6582', fontSize: '0.85rem', lineHeight: 1.5, maxWidth: 620 },
  count: { color: '#9a9aae', fontWeight: 600 },
  searchRow: { display: 'flex', gap: 10, marginBottom: 12 },
  input: { flex: 1, padding: '9px 12px', border: '1px solid #cfd5e6', borderRadius: 9, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' },
  primaryBtn: { background: '#2c63ff', color: '#fff', border: 0, borderRadius: 9, padding: '9px 18px', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' },
  smallBtn: { background: '#eef1f8', color: '#1f2a44', border: '1px solid #dfe4f0', borderRadius: 8, padding: '6px 12px', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem', marginRight: 8 },
  dangerBtn: { background: '#fde9ea', color: '#c0303a', border: '1px solid #f4c7cb', borderRadius: 8, padding: '6px 12px', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' },
  resultList: { listStyle: 'none', margin: 0, padding: 0 },
  resultRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid #eef0f5' },
  resultName: { fontWeight: 700, fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  resultEmail: { color: '#8a93a7', fontSize: '0.8rem' },
  tag: { background: '#e6f7ef', color: '#0f8a5f', borderRadius: 999, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 800 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' },
  th: { textAlign: 'left', padding: '8px 10px', color: '#66667a', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e6e6ea' },
  td: { padding: '11px 10px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top' },
  muted: { color: '#b3b9c6' },
  note: { color: '#8a93a7', fontSize: '0.88rem', padding: '6px 0' },
  error: { background: '#fde9ea', color: '#c0303a', border: '1px solid #f4c7cb', borderRadius: 9, padding: '10px 14px', marginBottom: 16, fontSize: '0.86rem' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(16,18,32,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, padding: '24px 26px', width: 460, maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalWho: { fontWeight: 700, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 2 },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#5a6582', margin: '12px 0 5px' },
  textarea: { width: '100%', padding: '9px 12px', border: '1px solid #cfd5e6', borderRadius: 9, fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 },
};
